// Bespoke voxel prefabs (civilians and scouts). Split out of creatures_extra.ts, whose
// CREATURE_PREFABS_EXTRA registry still assembles them.

import { HAIR_DARK, SKIN, box } from './creatures_extra_shared.js';
import type { CharacterPrefab } from './types.js';

export const settler: CharacterPrefab = {
  name: 'Settler',
  description: 'Pioneer with bedroll pack and straw hat',
  maxHp: 15,
  parts: [
    { name: 'head', voxels: box(-1, 11, -1, 3, 3, 3, SKIN) },
    { name: 'hat_brim', voxels: box(-2, 14, -2, 5, 1, 5, '#d4a04a') },
    { name: 'hat_crown', voxels: box(-1, 15, -1, 3, 1, 3, '#d4a04a') },
    {
      name: 'torso',
      voxels: [...box(-2, 6, -1, 5, 5, 3, '#4a7c3a'), { x: 0, y: 8, z: 1, color: '#d4a04a' }],
    },
    { name: 'left_arm', voxels: box(-3, 6, 0, 1, 5, 1, SKIN) },
    { name: 'right_arm', voxels: box(3, 6, 0, 1, 5, 1, SKIN) },
    {
      name: 'legs',
      voxels: [...box(-2, 0, 0, 2, 6, 1, '#704030'), ...box(1, 0, 0, 2, 6, 1, '#704030')],
    },
    { name: 'pack', voxels: box(-3, 7, 1, 1, 3, 2, '#8b6914') },
  ],
};

export const bronzeSmith: CharacterPrefab = {
  name: 'Bronze Smith',
  description: 'Stocky forge worker with hammer',
  maxHp: 20,
  parts: [
    { name: 'head', voxels: box(-1, 11, -1, 3, 3, 3, SKIN) },
    {
      name: 'beard',
      voxels: [
        { x: -1, y: 10, z: 2, color: '#6b3410' },
        { x: 0, y: 10, z: 2, color: '#6b3410' },
        { x: 1, y: 10, z: 2, color: '#6b3410' },
        { x: 0, y: 9, z: 2, color: '#6b3410' },
      ],
    },
    { name: 'hair', voxels: box(-1, 14, -1, 3, 1, 2, HAIR_DARK) },
    { name: 'torso', voxels: box(-2, 6, -1, 5, 5, 3, '#3a2818') },
    { name: 'apron', voxels: box(-2, 6, 1, 5, 5, 1, '#704030') },
    { name: 'left_arm', voxels: box(-3, 6, 0, 1, 5, 1, SKIN) },
    {
      name: 'right_arm_hammer',
      voxels: [
        ...box(3, 6, 0, 1, 4, 1, SKIN),
        ...box(3, 10, 0, 1, 3, 1, '#5a3a1a'),
        ...box(2, 12, -1, 3, 2, 3, '#888888'),
      ],
    },
    {
      name: 'legs',
      voxels: [...box(-2, 0, 0, 2, 6, 1, '#3a2818'), ...box(1, 0, 0, 2, 6, 1, '#3a2818')],
    },
  ],
};

export const engineer: CharacterPrefab = {
  name: 'Engineer',
  description: 'Builder with goggles and scroll',
  maxHp: 18,
  parts: [
    { name: 'head', voxels: box(-1, 11, -1, 3, 3, 3, SKIN) },
    {
      name: 'goggles',
      voxels: [
        { x: -1, y: 12, z: 2, color: '#444444' },
        { x: 1, y: 12, z: 2, color: '#444444' },
        { x: 0, y: 12, z: 2, color: '#222222' },
      ],
    },
    {
      name: 'cap',
      voxels: [...box(-2, 14, -1, 5, 1, 3, '#3a4a6a'), ...box(-1, 15, -1, 3, 1, 3, '#3a4a6a')],
    },
    {
      name: 'torso',
      voxels: [
        ...box(-2, 6, -1, 5, 5, 3, '#4a6080'),
        { x: -2, y: 5, z: 1, color: '#5a3a1a' },
        { x: -1, y: 5, z: 1, color: '#5a3a1a' },
        { x: 0, y: 5, z: 1, color: '#5a3a1a' },
        { x: 1, y: 5, z: 1, color: '#5a3a1a' },
        { x: 2, y: 5, z: 1, color: '#5a3a1a' },
      ],
    },
    { name: 'left_arm', voxels: box(-3, 6, 0, 1, 5, 1, '#4a6080') },
    {
      name: 'right_arm_scroll',
      voxels: [...box(3, 6, 0, 1, 4, 1, '#4a6080'), ...box(3, 9, -1, 1, 2, 3, '#e8d878')],
    },
    {
      name: 'legs',
      voxels: [...box(-2, 0, 0, 2, 6, 1, '#2a3a5a'), ...box(1, 0, 0, 2, 6, 1, '#2a3a5a')],
    },
  ],
};

// ─── Explorer (explorer) ────────────────────────────────────
// Scout: leather armor, wide-brim hat, pack on the back, binoculars at the neck.
// Reads as a Pathfinder / expeditionary rather than a generic bandit.
export const explorer: CharacterPrefab = {
  name: 'Explorer',
  description: 'Scout with leather armor, wide-brim hat, pack, and binoculars',
  maxHp: 16,
  parts: [
    { name: 'head', voxels: box(-1, 11, -1, 3, 3, 3, SKIN) },
    {
      name: 'hat_wide_brim',
      voxels: [...box(-2, 14, -2, 5, 1, 5, '#6a4a2a'), ...box(-1, 15, -1, 3, 1, 3, '#6a4a2a')],
    },
    {
      name: 'torso_leather',
      voxels: box(-2, 6, -1, 5, 5, 3, '#6a4a2a'),
    },
    {
      name: 'belt_pouches',
      voxels: [
        ...box(-2, 5, 1, 5, 1, 1, '#3a2a1a'),
        // Side pouches
        { x: -3, y: 6, z: 1, color: '#3a2a1a' },
        { x: 3, y: 6, z: 1, color: '#3a2a1a' },
      ],
    },
    {
      name: 'pack_back',
      // Frame pack on the back
      voxels: [
        ...box(-2, 6, -2, 3, 4, 1, '#5a3a1a'),
        // Bedroll on top
        { x: -1, y: 10, z: -2, color: '#4a6a4a' },
        { x: 0, y: 10, z: -2, color: '#4a6a4a' },
        { x: 1, y: 10, z: -2, color: '#4a6a4a' },
        // Cooking pot hanging off the side
        { x: -2, y: 5, z: -2, color: '#3a3a3a' },
      ],
    },
    {
      name: 'binoculars',
      // Hanging at the neck / chest
      voxels: [
        { x: -1, y: 6, z: 2, color: '#3a3a3a' },
        { x: 1, y: 6, z: 2, color: '#3a3a3a' },
        // Lens glint
        { x: -1, y: 6, z: 2, color: '#88ccff' },
        { x: 1, y: 6, z: 2, color: '#88ccff' },
        // Strap
        { x: 0, y: 7, z: 2, color: '#3a2a1a' },
      ],
    },
    {
      name: 'left_arm',
      voxels: box(-3, 6, 0, 1, 5, 1, '#6a4a2a'),
    },
    {
      name: 'right_arm_staff',
      voxels: [
        ...box(3, 6, 0, 1, 5, 1, '#6a4a2a'),
        // Walking staff
        ...box(3, 0, 1, 1, 12, 1, '#5a3a1a'),
        // Compass mounted on top
        { x: 3, y: 12, z: 1, color: '#aa8830', metalness: 1.0, roughness: 0.4 },
      ],
    },
    {
      name: 'legs',
      voxels: [
        ...box(-2, 0, 0, 2, 5, 1, '#5a3a1a'),
        ...box(1, 0, 0, 2, 5, 1, '#5a3a1a'),
        // Boots
        { x: -2, y: 0, z: 1, color: '#3a2a1a' },
        { x: -1, y: 0, z: 1, color: '#3a2a1a' },
        { x: 1, y: 0, z: 1, color: '#3a2a1a' },
        { x: 2, y: 0, z: 1, color: '#3a2a1a' },
      ],
    },
  ],
};
